import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/roles";
import { Toaster } from "@/components/ui/sonner";
import BuyerLayout from "@/components/BuyerLayout";
import SupplierLayout from "@/components/SupplierLayout";
import AdminLayout from "@/components/AdminLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import RegisterBuyer from "@/pages/RegisterBuyer";
import RegisterEnterprise from "@/pages/RegisterEnterprise";
import AcceptInvitation from "@/pages/AcceptInvitation";
import EnterpriseLayout from "@/components/EnterpriseLayout";
import EnterpriseDashboard from "@/pages/enterprise/EnterpriseDashboard";
import EnterpriseBuyers from "@/pages/enterprise/EnterpriseBuyers";
import EnterpriseSubscription from "@/pages/enterprise/EnterpriseSubscription";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminDemoRequests from "@/pages/admin/AdminDemoRequests";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSalesRequests from "@/pages/admin/AdminSalesRequests";
import EnterpriseSupplierRequests from "@/pages/enterprise/EnterpriseSupplierRequests";
import ChatPage from "@/components/ChatPage";
import SupportPage from "@/components/SupportPage";
import RegisterSeller from "@/pages/RegisterSeller";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PODetail from "@/pages/PODetail";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import PendingActions from "@/pages/PendingActions";
import Communications from "@/pages/Communications";
import Reminders from "@/pages/Reminders";
import Analytics from "@/pages/Analytics";
import SpendVisibility from "@/pages/buyer/SpendVisibility";
import Reports from "@/pages/Reports";
import ImportPO from "@/pages/ImportPO";
import SettingsPage from "@/pages/Settings";
import ActionLink from "@/pages/ActionLink";
import SupplierDashboard from "@/pages/supplier/SupplierDashboard";
import SupplierPOs from "@/pages/supplier/SupplierPOs";
import SupplierPODetail from "@/pages/supplier/SupplierPODetail";
import SupplierProfile from "@/pages/supplier/SupplierProfile";
import SupplierDocuments from "@/pages/supplier/SupplierDocuments";
import Enquiries from "@/pages/Enquiries";
import EnquiryDetail from "@/pages/EnquiryDetail";
import SupplierEnquiries from "@/pages/supplier/SupplierEnquiries";
import SupplierEnquiryDetail from "@/pages/supplier/SupplierEnquiryDetail";
import TechnicalReviewers from "@/pages/TechnicalReviewers";
import Approvers from "@/pages/Approvers";
import ApproverLayout from "@/components/ApproverLayout";
import ApproverDashboard from "@/pages/approver/ApproverDashboard";
import ApproverApprovals from "@/pages/approver/ApproverApprovals";
import ApproverApprovalDetail from "@/pages/approver/ApproverApprovalDetail";
import ApproverProfile from "@/pages/approver/ApproverProfile";
import TechnicalReviewerLayout from "@/components/TechnicalReviewerLayout";
import ReviewerDashboard from "@/pages/reviewer/ReviewerDashboard";
import ReviewerReviews from "@/pages/reviewer/ReviewerReviews";
import ReviewerReviewDetail from "@/pages/reviewer/ReviewerReviewDetail";
import ReviewerProfile from "@/pages/reviewer/ReviewerProfile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBuyers from "@/pages/admin/AdminBuyers";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminPOs from "@/pages/admin/AdminPOs";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AuditLogs from "@/pages/admin/AuditLogs";

function Loading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
      <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto opacity-90" />
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
    </div>
  );
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={roleHome(user.role)} replace />;
  return <Landing />;
}

function SellerLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={location.pathname.replace(/^\/supplier/, "/seller")} replace />;
}

function LegacyRedirect() {  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  const base = user.role === "org_admin" ? "/admin" : user.role === "enterprise_admin" ? "/enterprise" : user.role === "supplier" ? "/supplier" : "/buyer";
  let path = location.pathname;
  if (params.id) path = path.replace(`/${params.id}`, `/${params.id}`);
  return <Navigate to={`${base}${path}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register/buyer" element={<RegisterBuyer />} />
            <Route path="/register/enterprise" element={<RegisterEnterprise />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/register/seller" element={<RegisterSeller />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/action/:token" element={<ActionLink />} />

            <Route element={<RequireRole roles={["buyer", "procurement_manager"]}><BuyerLayout /></RequireRole>}>
              <Route path="/buyer/dashboard" element={<Dashboard />} />
              <Route path="/buyer/pos" element={<PurchaseOrders />} />
              <Route path="/buyer/pos/:id" element={<PODetail />} />
              <Route path="/buyer/enquiries" element={<Enquiries />} />
              <Route path="/buyer/enquiries/:id" element={<EnquiryDetail />} />
              <Route path="/buyer/technical-reviewers" element={<TechnicalReviewers />} />
              <Route path="/buyer/approvers" element={<Approvers />} />
              <Route path="/buyer/suppliers" element={<Suppliers />} />
              <Route path="/buyer/suppliers/:id" element={<SupplierDetail />} />
              <Route path="/buyer/pending" element={<PendingActions />} />
              <Route path="/buyer/communications" element={<Communications />} />
              <Route path="/buyer/chat" element={<ChatPage />} />
              <Route path="/buyer/support" element={<SupportPage />} />
              <Route path="/buyer/reminders" element={<Reminders />} />
              <Route path="/buyer/analytics" element={<Analytics />} />
              <Route path="/buyer/analytics/spend-visibility" element={<SpendVisibility />} />
              <Route path="/buyer/reports" element={<Reports />} />
              <Route path="/buyer/import" element={<ImportPO />} />
              <Route path="/buyer/settings" element={<SettingsPage />} />
            </Route>

            <Route element={<RequireRole roles={["enterprise_admin"]}><EnterpriseLayout /></RequireRole>}>
              <Route path="/enterprise/dashboard" element={<EnterpriseDashboard />} />
              <Route path="/enterprise/buyers" element={<EnterpriseBuyers />} />
              <Route path="/enterprise/supplier-requests" element={<EnterpriseSupplierRequests />} />
              <Route path="/enterprise/support" element={<SupportPage />} />
              <Route path="/enterprise/subscription" element={<EnterpriseSubscription />} />
            </Route>

            <Route element={<RequireRole roles={["org_admin"]}><AdminLayout /></RequireRole>}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/organization" element={<AdminOrganizations />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/buyers" element={<AdminBuyers />} />
              <Route path="/admin/suppliers" element={<AdminSuppliers />} />
              <Route path="/admin/pos" element={<AdminPOs />} />
              <Route path="/admin/pos/:id" element={<PODetail />} />
              <Route path="/admin/communications" element={<Communications />} />
              <Route path="/admin/automation" element={<Reminders />} />
              <Route path="/admin/templates" element={<AdminTemplates />} />
              <Route path="/admin/analytics" element={<Analytics />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/demo-requests" element={<AdminDemoRequests />} />
              <Route path="/admin/support" element={<AdminSupport />} />
              <Route path="/admin/sales-requests" element={<AdminSalesRequests />} />
              <Route path="/admin/audit" element={<AuditLogs />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
            </Route>

            <Route element={<RequireRole roles={["supplier"]}><SupplierLayout /></RequireRole>}>
              <Route path="/seller/dashboard" element={<SupplierDashboard />} />
              <Route path="/seller/pos" element={<SupplierPOs />} />
              <Route path="/seller/updates" element={<SupplierPOs requireAction />} />
              <Route path="/seller/pos/:id" element={<SupplierPODetail />} />
              <Route path="/seller/enquiries" element={<SupplierEnquiries />} />
              <Route path="/seller/enquiries/:inviteId" element={<SupplierEnquiryDetail />} />
              <Route path="/seller/communication" element={<Communications />} />
              <Route path="/seller/chat" element={<ChatPage />} />
              <Route path="/seller/support" element={<SupportPage />} />
              <Route path="/seller/documents" element={<SupplierDocuments />} />
              <Route path="/seller/profile" element={<SupplierProfile />} />
            </Route>

            <Route element={<RequireRole roles={["technical_reviewer"]}><TechnicalReviewerLayout /></RequireRole>}>
              <Route path="/technical-reviewer/dashboard" element={<ReviewerDashboard />} />
              <Route path="/technical-reviewer/reviews" element={<ReviewerReviews mode="pending" />} />
              <Route path="/technical-reviewer/completed" element={<ReviewerReviews mode="completed" />} />
              <Route path="/technical-reviewer/reviews/:id" element={<ReviewerReviewDetail />} />
              <Route path="/technical-reviewer/profile" element={<ReviewerProfile />} />
            </Route>

            <Route element={<RequireRole roles={["approver"]}><ApproverLayout /></RequireRole>}>
              <Route path="/approver/dashboard" element={<ApproverDashboard />} />
              <Route path="/approver/approvals" element={<ApproverApprovals mode="pending" />} />
              <Route path="/approver/completed" element={<ApproverApprovals mode="completed" />} />
              <Route path="/approver/approvals/:id" element={<ApproverApprovalDetail />} />
              <Route path="/approver/profile" element={<ApproverProfile />} />
            </Route>

            <Route path="/supplier/*" element={<SellerLegacyRedirect />} />

            <Route path="/dashboard" element={<LegacyRedirect />} />
            <Route path="/pos" element={<LegacyRedirect />} />
            <Route path="/pos/:id" element={<LegacyRedirect />} />
            <Route path="/suppliers" element={<LegacyRedirect />} />
            <Route path="/suppliers/:id" element={<LegacyRedirect />} />
            <Route path="/communications" element={<LegacyRedirect />} />
            <Route path="/reminders" element={<LegacyRedirect />} />
            <Route path="/analytics" element={<LegacyRedirect />} />
            <Route path="/import" element={<LegacyRedirect />} />
            <Route path="/settings" element={<LegacyRedirect />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
